/**
 * bods.js — Plymouth Bus App API utilities
 *
 * All requests go through /api/bods (Vercel proxy) to keep the API key secret.
 * Plymouth Citybus operator reference on BODS: PLCB
 * Plymouth admin area code: 163
 */

const PROXY = "/api/bods";
const PLYMOUTH_OPERATOR = "PLCB";
const PLYMOUTH_ADMIN_AREA = "118";
const PLYMOUTH_BBOX = "-4.235,50.330,-4.000,50.460";

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildUrl(endpoint, params = {}) {
  const qs = new URLSearchParams({ endpoint, ...params });
  return `${PROXY}?${qs}`;
}

// Bounding box ~1km around a lat/lng
export function getBoundingBox(lat, lng, radiusDeg = 0.008) {
  return `${(lng - radiusDeg).toFixed(5)},${(lat - radiusDeg).toFixed(5)},${(lng + radiusDeg).toFixed(5)},${(lat + radiusDeg).toFixed(5)}`;
}

// ─── Timetable / Dataset API ─────────────────────────────────────────────────

/**
 * Get all Plymouth Citybus timetable datasets
 * Returns JSON list of dataset metadata including download URLs
 */
export async function getPlymouthDatasets() {
  const url = buildUrl("dataset", {
    operatorRef: PLYMOUTH_OPERATOR,
    status: "published",
    limit: 25,
  });
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Dataset fetch failed: ${res.status}`);
  return res.json();
}

/**
 * Get datasets covering a bounding box
 * Useful for finding which operators serve an area
 */
export async function getDatasetsByBoundingBox(lat, lng) {
  const url = buildUrl("dataset", {
    boundingBox: getBoundingBox(lat, lng),
    status: "published",
    limit: 10,
  });
  const res = await fetch(url);
  if (!res.ok) throw new Error(`BBox dataset fetch failed: ${res.status}`);
  return res.json();
}

// ─── Real-time Vehicle Locations (SIRI-VM) ────────────────────────────────────

/**
 * Get live vehicle positions for Plymouth Citybus
 * Returns raw SIRI-VM XML — parse with parseSiriVm()
 */
export async function getLiveVehicles() {
  // Use a Plymouth bounding box rather than one operator code. BODS vehicle
  // feeds can expose Plymouth Citybus, Stagecoach and other local vehicles
  // with different operator references, so a single operatorRef misses useful
  // live matches.
  const url = buildUrl("datafeed", { boundingBox: PLYMOUTH_BBOX });
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Datafeed fetch failed: ${res.status}`);
  const xml = await res.text();
  return parseSiriVm(xml);
}

/**
 * Parse SIRI-VM XML into usable vehicle objects
 * Returns array of { vehicleRef, lat, lng, route, destination, bearing, recordedAt }
 */
export function parseSiriVm(xmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, "application/xml");
  const activities = doc.querySelectorAll("VehicleActivity");

  return Array.from(activities).map((a) => {
    const get = (tag) => a.querySelector(tag)?.textContent?.trim() ?? "";
    return {
      vehicleRef: get("VehicleRef"),
      lat: parseFloat(get("Latitude")) || null,
      lng: parseFloat(get("Longitude")) || null,
      route: get("PublishedLineName") || get("LineRef"),
      destination: get("DestinationName"),
      bearing: parseFloat(get("Bearing")) || 0,
      recordedAt: get("RecordedAtTime"),
      operatorRef: get("OperatorRef"),
    };
  }).filter((v) => v.lat && v.lng);
}

// ─── NaPTAN Stop Lookup ───────────────────────────────────────────────────────

/**
 * Local fallback Plymouth stops from NaPTAN.
 * The app normally uses /api/stops to load all Plymouth stops from the DfT NaPTAN API.
 * ATCO codes are the standard UK bus stop identifier.
 */
export const PLYMOUTH_STOPS = [
  { id: "1900HA000191", name: "Royal Parade (Stop A)", lat: 50.3714, lng: -4.1425, routes: ["1","2","21","25C","34"] },
  { id: "1900HA000192", name: "Royal Parade (Stop B)", lat: 50.3712, lng: -4.1420, routes: ["5","6","City","X80"] },
  { id: "1900HA000210", name: "Drake Circus (Stop C)", lat: 50.3726, lng: -4.1385, routes: ["1","2","5","City"] },
  { id: "1900HA000301", name: "Derry's Cross (Stop D)", lat: 50.3706, lng: -4.1448, routes: ["2","21","34"] },
  { id: "1900HA000401", name: "Bretonside (Stop E)", lat: 50.3701, lng: -4.1402, routes: ["City","25C","X80"] },
  { id: "1900HA000501", name: "Mutley Plain (Stop F)", lat: 50.3769, lng: -4.1372, routes: ["1","5","6"] },
  { id: "1900HA000601", name: "Plymstock Broadway", lat: 50.3612, lng: -4.0987, routes: ["21","22"] },
  { id: "1900HA000701", name: "Devonport (Stop A)", lat: 50.3739, lng: -4.1843, routes: ["2","34","X80"] },
];

/**
 * Find stops within radius (km) of a location
 */
export function getNearbyStops(lat, lng, radiusKm = 0.8, stops = PLYMOUTH_STOPS) {
  return stops
    .map((stop) => ({
      ...stop,
      distanceKm: haversineKm(lat, lng, stop.lat, stop.lng),
    }))
    .filter((s) => s.distanceKm <= radiusKm)
    .sort((a, b) => a.distanceKm - b.distanceKm);
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ─── Plymouth Routes reference data ──────────────────────────────────────────

export const PLYMOUTH_ROUTES = [
  { num: "1",    name: "Peverell — City Centre — Plymstock",     freq: "Every 10 min", colour: "#003087" },
  { num: "2",    name: "Devonport — City Centre — Plymstock",    freq: "Every 12 min", colour: "#003087" },
  { num: "5",    name: "City Centre — Efford",                   freq: "Every 20 min", colour: "#003087" },
  { num: "6",    name: "City Centre — Plympton",                 freq: "Every 15 min", colour: "#003087" },
  { num: "21",   name: "City Centre — Plymstock",                freq: "Every 15 min", colour: "#003087" },
  { num: "22",   name: "City Centre — Wembury",                  freq: "Every 30 min", colour: "#003087" },
  { num: "25C",  name: "City Centre — Crownhill",                freq: "Every 30 min", colour: "#003087" },
  { num: "34",   name: "City Centre — St Budeaux",               freq: "Every 20 min", colour: "#185FA5" },
  { num: "City", name: "City Centre Loop (Barbican circular)",   freq: "Every 8 min",  colour: "#1D9E75" },
  { num: "X80",  name: "Plymouth — Exeter (express)",            freq: "Hourly",       colour: "#A32D2D" },
];

// ─── Mock departures (fallback when BODS is unavailable) ─────────────────────

const NOW = new Date();
function minsFromNow(m) {
  const d = new Date(NOW.getTime() + m * 60000);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export const MOCK_DEPARTURES = {
  "1900HA000191": [
    { route: "1",   dest: "Peverell",   via: "Mutley Plain",  time: minsFromNow(2),  mins: 2  },
    { route: "21",  dest: "Plymstock",  via: "Laira Bridge",  time: minsFromNow(4),  mins: 4  },
    { route: "2",   dest: "Devonport",  via: "Union Street",  time: minsFromNow(9),  mins: 9  },
    { route: "25C", dest: "Crownhill",  via: "Hyde Park",     time: minsFromNow(14), mins: 14 },
    { route: "1",   dest: "Peverell",   via: "Mutley Plain",  time: minsFromNow(19), mins: 19 },
  ],
  "1900HA000192": [
    { route: "City",dest: "Barbican Loop", via: "Hoe",        time: minsFromNow(1),  mins: 1  },
    { route: "5",   dest: "Efford",     via: "Laira",         time: minsFromNow(8),  mins: 8  },
    { route: "6",   dest: "Plympton",   via: "Marsh Mills",   time: minsFromNow(11), mins: 11 },
  ],
  "1900HA000210": [
    { route: "1",   dest: "Plymstock",  via: "City",          time: minsFromNow(3),  mins: 3  },
    { route: "2",   dest: "Devonport",  via: "Union St",      time: minsFromNow(7),  mins: 7  },
    { route: "City",dest: "Barbican Loop", via: "Hoe",        time: minsFromNow(12), mins: 12 },
  ],
  "1900HA000301": [
    { route: "2",   dest: "Devonport",  via: "Union Street",  time: minsFromNow(3),  mins: 3  },
    { route: "34",  dest: "St Budeaux", via: "Ford",          time: minsFromNow(13), mins: 13 },
    { route: "21",  dest: "Plymstock",  via: "Cattedown",     time: minsFromNow(22), mins: 22 },
  ],
  "1900HA000401": [
    { route: "City",dest: "Barbican Loop", via: "Hoe",        time: minsFromNow(5),  mins: 5  },
    { route: "25C", dest: "Crownhill",  via: "Hyde Park",     time: minsFromNow(16), mins: 16 },
    { route: "X80", dest: "Exeter",     via: "A38",           time: minsFromNow(28), mins: 28 },
  ],
  "1900HA000501": [
    { route: "1",   dest: "City Centre",via: "Mutley",        time: minsFromNow(4),  mins: 4  },
    { route: "5",   dest: "Efford",     via: "Laira",         time: minsFromNow(9),  mins: 9  },
  ],
};
